// client/src/features/quotations/ItemLookupPanel.tsx
//
// THE MERGED CATALOGUE + SUPPLIER LOOKUP PANEL.
//
// One list, every row comparable, one unit selector at the top that restates
// every row — replacing what used to be two separate popups a quotation line
// opened: `components/MasterCatalogueSearchPopup.tsx` (Master Catalogue rows,
// a per-row unit picker) and `components/SupplierDropdown.tsx` (vendor-
// invoice / historical-quotation / price-list rows, priced per the line's own
// unit by the server). The product owner asked for the merge so a catalogue
// row and a supplier row for the same product are finally comparable at ONE
// unit instead of living in two popups that never compared — see
// `itemLookupRows.ts`, the pure module both row kinds are priced through so
// they cannot drift the way the two popups' independent unit arithmetic
// already had.
//
// WIRED IN as of 2026-09-23: `QuotationDetailPage`'s magnifier and its
// Supplier-cell opener (`features/quotations/SupplierCell.tsx`) both open
// this ONE panel now, and `MasterCatalogueSearchPopup` /
// `SupplierDropdown` are deleted. Which line is open stays page state (one
// panel instance, rendered once, outside the row loop) — the same "one open
// at a time" rule `SupplierCell`'s own header comment describes.
//
// `onSelectCatalogue` / `onSelectSupplier` are called with EXACTLY the
// arguments the two deleted paths used to pass
// (`MasterCatalogueSearchPopup`'s `onSelect(item, priceSelection)`,
// `QuotationDetailPage.handleSupplierSelect`'s `(itemIndex, supplierData)` —
// the `supplierData` half only, since which line is open is the PAGE's
// business, same as it was for `SupplierCell` before), so the page hands them
// straight to its existing `handleSearchSelect` / `handleSupplierSelect` —
// unchanged, pricing behaviour and all.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  searchMasterCatalogue,
  type MasterCatalogueItem,
  type CreateCatalogueItemPayload,
} from '../../lib/masterCatalogueApi';
import {
  fetchSupplierDropdown,
  type SupplierDropdownItem,
  type SupplierSource,
} from '../../lib/vendorInvoiceApi';
import { lookupItemPriceErrorMessage } from '../../lib/quotationApi';
import { describePackaging, conversionsForItem } from '../../utils/unitConversions';
import { isPseudoVendorName } from '../../utils/poSendStatus';
import { formatDate as fmtDate } from '../../utils/dateFormat';
import { currencyPrefix } from '../../utils/priceUtils';
import { supplierPriceOutliers } from './supplierOptionPrice';
import { priceSourceLabel } from './sourceLabel';
import { SupplierSourcesNote } from './SupplierSourcesNote';
import {
  isPricedRow,
  lookupUnitChoices,
  priceAtUnit,
  pricedFromIdentity,
  sortLookupRows,
  type CataloguePriceSelection,
  type LookupRow,
  type PriceAtUnitResult,
} from './itemLookupRows';
import { AddLineToCatalogueModal } from './AddLineToCatalogueModal';

interface ItemLookupPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Seeded into the search box, and the search runs on it — see the effect below. */
  initialQuery?: string;
  /** The line's own unit. Default of the panel's single unit selector. */
  lineUom?: string | null;
  /**
   * TT$ per 1 US$, the quotation's own locked rate — never today's, never a
   * fallback. `null` means no rate has loaded, in which case a catalogue row
   * offers no unit and a TTD supplier row is refused (see `itemLookupRows`).
   */
  quotationUsdToTtd: number | null;
  /**
   * The line's own `pricingDetails` — read (never re-derived) to find which
   * ONE row, if any, actually priced this line, so that row alone highlights.
   * See `itemLookupRows.pricedFromIdentity` for what is read off it and why a
   * vendor NAME is never used for this (owner, 2026-09-24: it used to be, and
   * every row sharing a supplier's name lit up at once).
   */
  pricingDetails?: unknown;
  /**
   * The Settings VAT rate (`getVatRate`, `lib/settingsApi.ts`), percent, or
   * null while it has not loaded / has never been set. Fed straight to
   * `itemLookupRows` — a VAT-applicable catalogue row offers no price at all
   * while this is null (`costPriceTiers.catalogueRowCostPrice`'s refusal),
   * the same treatment a missing `quotationUsdToTtd` already gets.
   */
  vatRatePercent: number | null;
  /** Optional: floats this quotation's client's preferred suppliers to the top (server-side). */
  quotationId?: string;
  onSelectCatalogue: (item: MasterCatalogueItem, selection?: CataloguePriceSelection) => void;
  /**
   * `searchTerm` is what the panel actually searched to produce the row being
   * picked (the query behind the CURRENTLY-SHOWN `supplierOptions`, not
   * whatever is mid-keystroke in the box) — F1, owner audit 2026-09-25. The
   * panel searches freely (2026-09-23), so a pick can name a genuinely
   * different product from the one on the line; the server resolves the
   * picked option from THIS term, never from the line's own name, which
   * refused a clean pick of an unrelated product ("Cabbage White" line,
   * "Lettuce Iceberg" pick — 409 SUPPLIER_OPTION_NOT_FOUND under the old scope).
   */
  onSelectSupplier: (option: SupplierDropdownItem, searchTerm: string) => void;
  triggerRef?: React.RefObject<HTMLElement>;
  /**
   * The footer's "+ Add this item to Master Catalogue" — ALWAYS offered, even
   * when the search above found matches (the owner was explicit: a fuzzy,
   * wrong-looking match still wants this, not only an empty result).
   *
   * Opens `CatalogueItemForm` (the SAME form the Master Catalogue's own Add
   * item dialog and the Review page's edit-then-approve use — never a fork),
   * seeded from this line's own `initialQuery` / `lineUom`, and hands its
   * `onSubmitOverride` straight to this prop: the caller owns what "saving"
   * means here, same as the Review page owns it for its own override. This
   * panel does not know — and must not need to know — that saving means
   * BOTH submitting the addition for review AND pricing the line from it
   * immediately (`QuotationDetailPage`'s own doc comment on its
   * implementation has the two-things-happen story).
   */
  onAddToCatalogue: (payload: CreateCatalogueItemPayload) => Promise<MasterCatalogueItem | null>;
}

/**
 * Flexible grid, `minmax(0,1fr)` on every text column — unlike
 * `MasterCatalogueSearchPopup`'s fixed 1260px table, this must not overflow a
 * narrow window. Item Name gets the largest share: it is the longest text in
 * the row, same reasoning as `SupplierDropdown.GRID_COLUMNS`.
 *
 * Packaging widened (owner, 2026-09-24) so "BAG of 12 x 5 KG" fits on one
 * line instead of wrapping across the row's two-line clamp — taken from Item
 * Name and Supplier, not added: the three text columns' shares moved from
 * 1.6 / 1 / 1 to 1.3 / 1.3 / 0.9, so the grid's total width is unchanged.
 *
 * Source is 190px so the widest badge, "HISTORICAL · 23/04/2026", shows its
 * year; at 130px every dated badge was cut to "CATALOGUE · 18/08/".
 */
const GRID_COLUMNS = 'minmax(0,1.3fr) minmax(0,1.3fr) minmax(0,0.9fr) 110px 190px';

const SEARCH_DEBOUNCE_MS = 300;

function catalogueRowId(item: MasterCatalogueItem): string {
  return `catalogue-${item._id}`;
}

function supplierRowId(option: SupplierDropdownItem, index: number): string {
  return `supplier-${index}-${option.vendorName}-${option.description}`;
}

/** The Supplier column's owner check, applied to whichever name a row carries. */
function shownSupplierName(name: string | null | undefined): string | null {
  return name && !isPseudoVendorName(name) ? name : null;
}

/**
 * The row's own stated price, in its own currency — the one survivor of what
 * was a deliberate temporary duplication: `SupplierDropdown` had its own
 * private `formatPrice` doing the same job, and that component (and the
 * duplicate) is deleted now that this panel is the only lookup. Pure
 * presentation, not money math: no rate, no rounding (so not
 * `formatCurrency`, which ceils) — the label is the owner's, `currencyPrefix`
 * (BUSINESS_RULES#quotation-screen-money-labels: "US$", never a bare "$").
 */
function nativePriceLabel(amount: number, currency?: string | null): string {
  const safe = Number.isFinite(amount) ? amount : 0;
  return `${currencyPrefix(currency)}${safe.toFixed(2)}`;
}

export const ItemLookupPanel: React.FC<ItemLookupPanelProps> = ({
  isOpen,
  onClose,
  initialQuery = '',
  lineUom,
  quotationUsdToTtd,
  pricingDetails = null,
  vatRatePercent,
  quotationId,
  onSelectCatalogue,
  onSelectSupplier,
  triggerRef,
  onAddToCatalogue,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogueItems, setCatalogueItems] = useState<MasterCatalogueItem[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<SupplierDropdownItem[]>([]);
  /**
   * The search text that produced `supplierOptions` — captured at fetch time,
   * never read from `searchQuery` at click time (F1, owner audit 2026-09-25).
   * A pick must name the search that is ACTUALLY on screen, even if the box
   * has since been typed into again and the next debounce has not landed —
   * `supplierOptions` itself has not changed either in that window, so this
   * stays the term that produced the row being clicked.
   */
  const [supplierOptionsQuery, setSupplierOptionsQuery] = useState('');
  const [loadingCatalogue, setLoadingCatalogue] = useState(false);
  const [loadingSupplier, setLoadingSupplier] = useState(false);
  /** The "+ Add this item to Master Catalogue" form, open over this panel. */
  const [addToCatalogueOpen, setAddToCatalogueOpen] = useState(false);
  // Kept SEPARATE, never folded into one "something went wrong" flag: the
  // catalogue search failing and the supplier lookup failing are different
  // facts, and this panel is where someone decides an item is not in the
  // catalogue yet and submits it for review — a dropped request that renders
  // as "No items found" makes them file a duplicate of a row that already
  // exists. A failure must never render as an empty result (CLAUDE.md: no
  // silent-fallback patterns).
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const [supplierError, setSupplierError] = useState<string | null>(null);
  // D99.3: sources the server could not read, so a partial list says it is partial.
  const [unavailableSources, setUnavailableSources] = useState<SupplierSource[]>([]);
  // null = "follow the default" (the line's unit, or the first catalogue-
  // derived choice once one exists). Set only once the person picks from the
  // selector themselves.
  const [chosenUnit, setChosenUnit] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Seeded, not blank — same trick and the same reason as
      // `MasterCatalogueSearchPopup`: `initialQuery` is the line's own item
      // name when this was opened from a quotation row, and setting it here
      // is what runs the search (the debounced effects below fire on the
      // change).
      setSearchQuery(initialQuery);
      setCatalogueItems([]);
      setSupplierOptions([]);
      setSupplierOptionsQuery('');
      setCatalogueError(null);
      setSupplierError(null);
      setChosenUnit(null);
      setAddToCatalogueOpen(false);
      // Loading from the first frame when a query is already in hand. The
      // debounced effects below only flip loading true AFTER the 300ms wait,
      // so without this a seeded open shows "No items found" for a third of a
      // second before the results it is already fetching arrive — a wrong
      // answer, and the one people screenshot.
      setLoadingCatalogue(!!initialQuery);
      setLoadingSupplier(!!initialQuery);
    }
  }, [isOpen, initialQuery]);

  const unitChoices = useMemo(
    () => lookupUnitChoices({ lineUom, catalogueItems, quotationUsdToTtd, vatRatePercent }),
    [lineUom, catalogueItems, quotationUsdToTtd, vatRatePercent]
  );
  const effectiveUnit = chosenUnit ?? unitChoices[0] ?? '';

  // Both searches below are keyed on `isOpen` as well as the query, so EVERY
  // open searches again (client call 2026-09-24, streaky bacon). This panel
  // stays mounted while closed, so reopening it on the same line re-seeds
  // `searchQuery` with the string it already holds — a no-op update that
  // re-ran neither effect, while the open effect above had already emptied
  // the rows and set loading: "Loading..." forever, until another line's name
  // changed the query. A closed panel searches nothing.
  //
  // Catalogue: debounced on the query alone. A catalogue row re-picks its
  // matching `CostUnitOption` locally when the unit changes — no refetch.
  useEffect(() => {
    if (!isOpen) return;
    const query = searchQuery.trim();
    if (!query) {
      setCatalogueItems([]);
      setCatalogueError(null);
      // Reset here too: a reopen on a BLANK line runs this effect once with
      // the previous line's query (loading on) before '' lands.
      setLoadingCatalogue(false);
      return;
    }
    let cancelled = false;
    setLoadingCatalogue(true);
    setCatalogueError(null);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await searchMasterCatalogue(query);
        if (cancelled) return;
        // `success: false` is a real, distinguishable answer from the server
        // — not a synonym for zero matches. `MasterCatalogueSearchPopup`
        // treats the two the same (silently keeps whatever was already on
        // screen); that is the worse of the two behaviours this panel
        // merges, and merging must keep the better one, not this one.
        if (response.success) {
          setCatalogueItems(response.data);
        } else {
          setCatalogueItems([]);
          setCatalogueError('The Master Catalogue search did not return a result. Try again.');
        }
      } catch (error: any) {
        if (cancelled) return;
        console.error('Failed to search catalogue:', error);
        setCatalogueItems([]);
        // Fixed sentence, never `error.message`: apiClient builds EVERY 4xx/5xx
        // Error from the server's message, so it can be raw server prose
        // (invariant 6).
        setCatalogueError('The Master Catalogue search failed.');
      } finally {
        if (!cancelled) setLoadingCatalogue(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isOpen, searchQuery]);

  // Supplier: debounced on the query AND on the chosen unit. The server is
  // the one owner of "what does this option cost per this unit" — changing
  // the unit selector means asking it again, never converting client-side.
  useEffect(() => {
    if (!isOpen) return;
    const query = searchQuery.trim();
    if (!query) {
      setSupplierOptions([]);
      setSupplierOptionsQuery('');
      setUnavailableSources([]);
      setSupplierError(null);
      setLoadingSupplier(false);
      return;
    }
    let cancelled = false;
    setLoadingSupplier(true);
    setSupplierError(null);
    const timeoutId = setTimeout(async () => {
      try {
        const { options: data, unavailableSources: unread } = await fetchSupplierDropdown(query, quotationId, effectiveUnit);
        if (!cancelled) {
          setSupplierOptions(data);
          setUnavailableSources(unread);
          // The search THIS list came from — set in the same tick as the rows
          // it describes, so a pick can never name a different search from the
          // one that produced the row on screen (F1, owner audit 2026-09-25).
          setSupplierOptionsQuery(query);
        }
      } catch (error: any) {
        if (cancelled) return;
        console.error('Failed to load supplier options:', error);
        setSupplierOptions([]);
        setUnavailableSources([]);
        // A fixed sentence worded from the error's CODE, as the catalogue branch above (invariant 6): a
        // missing VAT or TT$/US$ rate in Settings says so instead of "lookup failed: lookup failed" (B011).
        setSupplierError(lookupItemPriceErrorMessage(error, 'The supplier lookup failed.'));
      } finally {
        if (!cancelled) setLoadingSupplier(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isOpen, searchQuery, effectiveUnit, quotationId]);

  // Click outside / Escape — same pattern as both components this replaces.
  //
  // NOT while the "+ Add this item" form is open over this panel. That form's
  // `Modal` renders through a PORTAL, so every click inside it lands outside
  // `panelRef` — and this handler used to close the whole panel (form and all)
  // on the first click into any of its fields. The form owns its own dismissal
  // (`useDialogDismiss`: backdrop, Escape, Cancel) while it is up.
  useEffect(() => {
    if (!isOpen || addToCatalogueOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const onTrigger = !!triggerRef?.current && triggerRef.current.contains(target);
      if (panelRef.current && !panelRef.current.contains(target) && !onTrigger) {
        onClose();
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, addToCatalogueOpen, onClose, triggerRef]);

  const rows: LookupRow[] = useMemo(
    () => [
      ...catalogueItems.map((item): LookupRow => ({ kind: 'catalogue', id: catalogueRowId(item), item })),
      ...supplierOptions.map(
        (option, index): LookupRow => ({ kind: 'supplier', id: supplierRowId(option, index), option })
      ),
    ],
    [catalogueItems, supplierOptions]
  );

  const priceOptsFor = (unit: string) => ({ unit, quotationUsdToTtd, lineUom, vatRatePercent });

  const sortedRows = useMemo(
    () => sortLookupRows(rows, (row) => priceAtUnit(row, priceOptsFor(effectiveUnit)), searchQuery),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, searchQuery, effectiveUnit, quotationUsdToTtd, lineUom, vatRatePercent]
  );

  const priceResults: PriceAtUnitResult[] = useMemo(
    () => sortedRows.map((row) => priceAtUnit(row, priceOptsFor(effectiveUnit))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedRows, effectiveUnit, quotationUsdToTtd, lineUom, vatRatePercent]
  );

  // Outliers across BOTH kinds at once — the real gain from merging: a
  // catalogue row ten times off the invoices now shows up, which it never
  // could while the two lived in separate popups. Same owner, same factor
  // (`supplierOptionPrice.supplierPriceOutliers`, DECISIONS D42) that already
  // flags this inside `SupplierDropdown`.
  const outliers = useMemo(
    () => supplierPriceOutliers(priceResults.map((r) => (r.ok ? r.usd : null))),
    [priceResults]
  );

  if (!isOpen) return null;

  const loading = loadingCatalogue || loadingSupplier;
  // Which ONE row, if any, actually priced this line — never a vendor-name
  // comparison. See `itemLookupRows.pricedFromIdentity`'s own header.
  const pricedFrom = pricedFromIdentity(pricingDetails);

  const vendorNameOf = (row: LookupRow): string | null =>
    row.kind === 'catalogue' ? row.item.supplier ?? null : row.option.vendorName ?? null;

  return (
    <div className="fixed inset-0 z-[70] overflow-auto p-4 modal-overlay-safe flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40 transition-opacity" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="relative z-10 bg-transparent flex flex-col pointer-events-auto w-full max-w-[min(1280px,95vw)] max-h-[min(85vh,720px)] overflow-auto rounded-md min-w-0"
      >
        {/* Search bar */}
        <div className="flex flex-row items-center px-4 gap-2 bg-primary-50 border border-[#D7DBD9] rounded-md shadow-sm mb-3 h-[42px] min-w-0">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="Search item…"
            className="flex-grow min-w-0 bg-transparent border-none outline-none text-slate-900 font-['Public_Sans'] font-medium text-sm leading-[22px] placeholder-gray-500"
            autoFocus
          />
          {/* Single unit selector — restates every row, at the top rather than
              per row, so the whole list stays one comparison instead of N. */}
          <label className="flex items-center gap-1.5 flex-shrink-0 text-xs text-slate-600 font-['Public_Sans']">
            Show prices per:
            <select
              value={effectiveUnit}
              onChange={(e) => setChosenUnit(e.target.value)}
              className="text-xs border border-[#D7DBD9] rounded px-1.5 py-1 bg-white text-primary-500 font-medium focus:outline-none cursor-pointer"
              disabled={unitChoices.length === 0}
              aria-label="Show prices per"
            >
              {unitChoices.length === 0 ? (
                <option value="">—</option>
              ) : (
                unitChoices.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))
              )}
            </select>
          </label>
          <button
            onClick={onClose}
            className="flex-shrink-0 flex items-center justify-center w-[32px] h-[32px] bg-white border border-[#D7DBD9] rounded hover:bg-[#e0e4e2] transition-colors"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Results table */}
        <div className="flex flex-col bg-white border border-[#D7DBD9] rounded-md overflow-hidden shadow-2xl min-w-0">
          <div
            className="grid items-center px-3 py-2 bg-[#F9FAF9] border-b border-[#D7DBD9] gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            <div>Item Name</div>
            <div>Packaging</div>
            <div>Supplier</div>
            <div className="text-right">Price</div>
            <div>Source</div>
          </div>

          {/* Each source's failure is its own line: the two fetches can fail
              independently, and conflating them into one banner would hide
              which half of the list to distrust. Shown ABOVE the rows — a
              source that failed can sit right beside rows the OTHER source
              still found, and hiding those behind the error would be its own
              silent-fallback (a failed catalogue search must not blank a
              successful supplier list, or the reverse). */}
          {(catalogueError || supplierError) && (
            <div className="px-3 py-2 border-b border-[#D7DBD9] bg-red-50 text-xs text-red-600 font-['Public_Sans'] space-y-0.5">
              {catalogueError && <div>Catalogue search failed: {catalogueError}</div>}
              {supplierError && <div>Supplier lookup failed: {supplierError}</div>}
            </div>
          )}

          <SupplierSourcesNote sources={unavailableSources} />

          <div style={{ maxHeight: 600, overflowY: 'auto' }}>
            {loading ? (
              <div className="flex items-center justify-center h-24 text-gray-500 font-['Public_Sans']">
                Loading...
              </div>
            ) : sortedRows.length === 0 ? (
              // A real error already explains an empty list above; claiming
              // "No items found" ON TOP of it would read as "the search ran
              // and found nothing", which is the one thing that is NOT true —
              // and is exactly the wrong answer someone reads here as
              // grounds to add a duplicate catalogue row.
              catalogueError || supplierError ? null : (
                <div className="flex items-center justify-center h-24 text-gray-500 font-['Public_Sans']">
                  {searchQuery ? 'No items found' : 'Start typing to search...'}
                </div>
              )
            ) : (
              <ul className="divide-y divide-[#EEF0EF]">
                {sortedRows.map((row, index) => {
                  const price = priceResults[index];
                  const unusable = !price.ok;
                  const vendorName = vendorNameOf(row);
                  const shownVendor = shownSupplierName(vendorName);
                  // The ONE row this line was priced from — an exact id match
                  // for a catalogue row, its own stated description for a
                  // supplier row, and never a vendor-name comparison (which
                  // used to light up every row from the same real-world
                  // supplier at once). See `itemLookupRows.isPricedRow`.
                  const isSelected = isPricedRow(row, pricedFrom);
                  const outlier = outliers.get(index);

                  let itemName: string;
                  let packaging: string;
                  let packagingIsGap = false;
                  let sourceText: string;
                  let rowTitle: string;

                  if (row.kind === 'catalogue') {
                    itemName = row.item.item_name;
                    const described = describePackaging(conversionsForItem(row.item), row.item.unit);
                    packagingIsGap = !described && !!row.item.packagingGap;
                    packaging = described || row.item.packagingGap || '—';
                    // D86: every row states where its price came from and the date it
                    // counts from, or "No date" — the same reading as the supplier rows.
                    sourceText = `${priceSourceLabel(row.item).label} · ${fmtDate(row.item.priceDate, 'No date')}`;
                    rowTitle = price.ok
                      ? isSelected
                        ? 'Currently priced on this line'
                        : `Apply ${row.item.item_name}`
                      : price.reason;
                  } else {
                    itemName = row.option.description || '—';
                    packaging = row.option.packaging || '—';
                    // `formatDate` — dd/mm/yyyy, the one date owner
                    // (`utils/dateFormat`) — beside the source badge, same
                    // information `SupplierDropdown`'s Date column carries,
                    // folded into Source now that this panel has one fewer
                    // column to spend on it.
                    const label = priceSourceLabel(row.option).label;
                    sourceText = `${label} · ${fmtDate(row.option.invoiceDate, 'No date')}`;
                    rowTitle = price.ok
                      ? isSelected
                        ? 'Currently selected supplier'
                        : `Apply ${row.option.vendorName}`
                      : price.reason;
                  }

                  return (
                    <li key={row.id}>
                      <button
                        type="button"
                        // The row already pricing this line cannot be "replaced" by itself: the
                        // pick would confirm and write the same price again (QA 2026-10-08).
                        disabled={unusable || isSelected}
                        onClick={() => {
                          if (!price.ok || isSelected) return;
                          if (row.kind === 'catalogue') onSelectCatalogue(row.item, price.catalogueSelection);
                          else onSelectSupplier(row.option, supplierOptionsQuery);
                        }}
                        title={rowTitle}
                        className={`w-full text-left grid items-center px-3 py-2.5 gap-2 transition-colors ${
                          unusable
                            ? 'opacity-60 cursor-not-allowed bg-[#FCFCFC]'
                            : isSelected
                            ? 'bg-cyan-50 cursor-default'
                            : 'hover:bg-[#F9FAF9]'
                        }`}
                        style={{ gridTemplateColumns: GRID_COLUMNS }}
                      >
                        <span
                          className="font-['Public_Sans'] text-xs leading-[16px] text-slate-900 line-clamp-2 break-words"
                          title={itemName}
                        >
                          {itemName}
                        </span>
                        <span
                          className={`font-['Public_Sans'] text-xs leading-[16px] line-clamp-2 break-words ${
                            packagingIsGap ? 'text-[#888]' : 'text-slate-600'
                          }`}
                          title={packaging === '—' ? 'This source does not state the packaging' : packaging}
                        >
                          {packaging}
                        </span>
                        <span
                          className={`font-['Public_Sans'] text-xs leading-[16px] truncate ${
                            isSelected ? 'font-semibold text-cyan-700' : 'text-slate-900'
                          }`}
                          title={shownVendor || '-'}
                        >
                          {shownVendor || '-'}
                          {isSelected ? ' ✓' : ''}
                        </span>
                        <span className="font-['Public_Sans'] text-xs leading-[16px] text-slate-900 text-right tabular-nums truncate flex flex-col items-end">
                          {row.kind === 'catalogue' ? (
                            price.ok && price.ttdWithVat != null ? (
                              <>
                                {/* The row's own stated figure — TT$, VAT-INCLUSIVE, and
                                    LABELLED so ("Cost Price (TT$, w/ VAT)" is the popup's
                                    own header for this figure). A TTD-native supplier row's
                                    native TT$ figure below is ALSO VAT-inclusive now (owner
                                    rule, Deborah's VAT report, 2026-09-24, D63 —
                                    `supplierOptions.service`'s `vatInclusiveCost` and
                                    `itemLookupRows`'s own header on the supplier row), so
                                    the two figures ARE the same kind of thing; the label
                                    stays because this cell states it, it does not assume the
                                    reader already knows it. */}
                                <span>
                                  {currencyPrefix('TTD')}{price.ttdWithVat.toFixed(2)}
                                  <span className="text-slate-500"> incl. VAT</span>
                                </span>
                                <span className="text-xs leading-[13px] text-slate-500">
                                  {currencyPrefix('USD')}{price.usd.toFixed(2)}
                                </span>
                              </>
                            ) : !price.ok && price.noPrice ? (
                              // R12: this row states NO price at any unit — the same fact
                              // a priceless supplier row's "no price" states below, so it
                              // reads the same way (grey, italic), not the generic "—" a
                              // wrong-UNIT refusal still uses (that row DOES have a price,
                              // just not one this selector can show here).
                              <span className="text-gray-400 italic">no price</span>
                            ) : (
                              <span>—</span>
                            )
                          ) : (
                            <>
                              {/* The supplier's own stated figure, in its own currency and
                                  unit — shown even when the row is refused, exactly as
                                  `SupplierDropdown` shows it today.

                                  `unitPrice == null` means the source stated NO price —
                                  `vendorInvoiceApi`'s DTO already folds a genuinely-stated
                                  $0.00 in with a missing one here (`positiveNumber`), so
                                  this one check covers both (owner, 2026-09-24: Cabbage
                                  White / Grapes White / Leeks White read "$0.00/KG" and
                                  were clickable at it). Never "$0.00" — missing != zero,
                                  and neither reading is a price to show in black. */}
                              <span>
                                {row.option.unitPrice == null ? (
                                  <span className="text-gray-400 italic">no price</span>
                                ) : (
                                  <span>{nativePriceLabel(row.option.unitPrice, row.option.currency)}</span>
                                )}
                                {row.option.priceUnit && (
                                  <span className="text-slate-500">/{row.option.priceUnit}</span>
                                )}
                              </span>
                              {price.ok &&
                                (() => {
                                  // Same restraint `SupplierDropdown` already shows: skip a
                                  // second US$ line that would just repeat the first one
                                  // (already US$, already per this unit).
                                  const stated = String(row.option.currency || '').trim().toUpperCase();
                                  const restated =
                                    !!row.option.lineUnitBasis && row.option.lineUnitBasis !== 'same_unit';
                                  const showsUsdToo = stated !== 'USD' || restated;
                                  return (
                                    showsUsdToo && (
                                      <span className="text-xs leading-[13px] text-slate-500">
                                        {currencyPrefix('USD')}{price.usd.toFixed(2)}
                                      </span>
                                    )
                                  );
                                })()}
                              {/* D78 (owner, 2026-09-26): the supplier printed no unit
                                  price; it was worked out from the line total (D75) and
                                  may not have been reviewed. Offered, marked — the same
                                  amber note style as the outlier flag below. */}
                              {row.option.unitPriceFromLineTotal === true && (
                                <span
                                  className="text-xs leading-[13px] font-semibold text-amber-700"
                                  title="The supplier printed no unit price. It was worked out by dividing the line total by the quantity. Check it against the invoice before quoting."
                                >
                                  worked out from line total
                                </span>
                              )}
                            </>
                          )}
                          {outlier && (
                            <span
                              className="text-xs leading-[13px] font-semibold text-amber-700"
                              title={`This price is ${outlier.times}x ${
                                outlier.direction === 'above' ? 'higher' : 'lower'
                              } than the other rows'. Check packaging before using it.`}
                            >
                              {outlier.direction === 'above' ? `⚠ ${outlier.times}x others` : `⚠ 1/${outlier.times} of others`}
                            </span>
                          )}
                        </span>
                        <span className="min-w-0 flex justify-start">
                          <span
                            className="inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap overflow-hidden text-ellipsis bg-slate-50 text-slate-700 border-slate-200"
                            title={sourceText}
                          >
                            {sourceText}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ALWAYS offered — not only when the search above found nothing.
              The owner was explicit: a fuzzy, wrong-looking match is still
              grounds to add the real item, not just an empty result. */}
          <div className="flex justify-start border-t border-[#D7DBD9] bg-[#F9FAF9] px-3 py-2">
            <button
              type="button"
              onClick={() => setAddToCatalogueOpen(true)}
              className="font-['Public_Sans'] text-xs font-semibold text-primary-600 hover:text-primary-700"
            >
              + Add this item to Master Catalogue
            </button>
          </div>
        </div>
      </div>

      {addToCatalogueOpen && (
        <AddLineToCatalogueModal
          // Exactly as they were when this panel opened (`initialQuery`/`lineUom`), never
          // whatever the search box currently holds.
          itemName={initialQuery}
          uom={lineUom}
          onClose={() => setAddToCatalogueOpen(false)}
          onSubmit={onAddToCatalogue}
        />
      )}
    </div>
  );
};

export default ItemLookupPanel;
